import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { successStories } from "../../data/placementSuccessStories";
import assetUrl from "../../services/assetUrl";

const pkgNum = (p = "") => { const m = String(p).match(/([\d.]+)/); return m ? parseFloat(m[1]) : 0; };
const ROTATE_MS = 6500;
const INITIAL = 8;
const initials = (n = "") => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const abbrev = (n = "") => { const w = n.trim().split(/\s+/); return (w.length >= 2 ? w[0][0] + w[1][0] : n.slice(0, 2)).toUpperCase(); };

const Avatar = ({ src, name }) => {
  const [err, setErr] = useState(false);
  if (!src || err) return <span className="plx-scard__init">{initials(name)}</span>;
  return <img src={assetUrl(src)} alt={name} loading="lazy" onError={() => setErr(true)} />;
};

// Master–Detail: a company list (left) drives a student grid (right).
// Uniform rows/chips keep everything aligned; auto-advances + scrolls the
// active company into view (pause on hover), click any company to jump.
const SuccessShowcase = ({ data }) => {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const source = data && Object.keys(data).length ? data : successStories;
  const COMPANIES = useMemo(
    () => Object.entries(source).map(([key, d]) => ({ key, ...d })).sort((a, b) => pkgNum(b.package) - pkgNum(a.package)),
    [source]
  );
  const n = COMPANIES.length;
  const co = COMPANIES[active] || COMPANIES[0] || { key: "", company: "", students: [], package: "" };
  const listRef = useRef(null);
  const activeRef = useRef(null);

  const go = useCallback((i) => { setActive(((i % n) + n) % n); setShowAll(false); }, [n]);

  useEffect(() => {
    if (reduce || paused) return undefined;
    const id = setTimeout(() => { setActive((a) => (a + 1) % n); setShowAll(false); }, ROTATE_MS);
    return () => clearTimeout(id);
  }, [active, paused, reduce, n]);

  // keep the active item visible — scroll only the list (vertical on desktop,
  // horizontal on the mobile strip), never the page
  useEffect(() => {
    const btn = activeRef.current, list = listRef.current;
    if (!btn || !list) return;
    const top = btn.offsetTop, bottom = top + btn.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top - 8;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 8;
    const left = btn.offsetLeft, right = left + btn.offsetWidth;
    if (left < list.scrollLeft) list.scrollLeft = left - 8;
    else if (right > list.scrollLeft + list.clientWidth) list.scrollLeft = right - list.clientWidth + 8;
  }, [active]);

  const students = showAll ? co.students : co.students.slice(0, INITIAL);
  const cardsV = { hidden: {}, show: { transition: { staggerChildren: 0.035 } } };
  const cardV = reduce
    ? { hidden: { opacity: 0 }, show: { opacity: 1 } }
    : { hidden: { opacity: 0, y: 14, scale: 0.97 }, show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } } };

  return (
    <div className="plx-md" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <span className="plx-md__bg" aria-hidden="true" />

      {/* company list */}
      <div className="plx-md__list" ref={listRef} role="tablist" aria-label="Recruiters">
        {COMPANIES.map((c, i) => {
          const on = i === active;
          return (
            <button
              key={c.key}
              type="button"
              role="tab"
              aria-selected={on}
              ref={on ? activeRef : null}
              className={`plx-md__co${on ? " is-active" : ""}`}
              onClick={() => go(i)}
            >
              <span className="plx-md__co-logo">{c.logo ? <img src={assetUrl(c.logo)} alt="" /> : <span>{abbrev(c.company)}</span>}</span>
              <span className="plx-md__co-info">
                <span className="plx-md__co-name">{c.company}</span>
                <span className="plx-md__co-sub">{c.students.length} placed · {c.package}</span>
              </span>
              <span className="plx-md__co-chev" aria-hidden="true">›</span>
            </button>
          );
        })}
      </div>

      {/* detail */}
      <div className="plx-md__detail">
          <motion.div
            key={co.key}
            className="plx-md__panel"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="plx-md__head">
              <div className="plx-md__head-logo">{co.logo ? <img src={assetUrl(co.logo)} alt={co.company} /> : <span>{abbrev(co.company)}</span>}</div>
              <div className="plx-md__head-txt">
                <h3>{co.company}</h3>
                <div className="plx-md__head-meta">
                  <span><strong>{co.students.length}</strong> placed</span>
                  <span className="plx-md__sep" />
                  <span>Package <strong>{co.package}</strong></span>
                </div>
              </div>
            </div>

            <motion.div className="plx-md__students" variants={cardsV} initial="hidden" animate="show">
              {students.map((s, i) => (
                <motion.article key={`${co.key}-${i}`} className="plx-scard" variants={cardV}>
                  <div className="plx-scard__avatar"><Avatar src={s.img} name={s.name} /></div>
                  <h4 className="plx-scard__name">{s.name}</h4>
                  <p className="plx-scard__branch">{s.branch}</p>
                  <span className="plx-scard__pkg">{s.package}</span>
                </motion.article>
              ))}
            </motion.div>

            {co.students.length > INITIAL && (
              <div className="plx-md__more">
                <button type="button" onClick={() => setShowAll((v) => !v)}>
                  {showAll ? "Show fewer" : `Show all ${co.students.length}`}
                </button>
              </div>
            )}
          </motion.div>
      </div>
    </div>
  );
};

export default SuccessShowcase;
